import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from '@phosphor-icons/react';

const COUNTER_KEY = 'welcomeSheetOpenCount';
const SESSION_FLAG = 'welcomeSheetCountedSession';
// Show on opens 1, 4, 7, 10... (every 3rd open: shows once, then skips 2)
const SHOW_EVERY_N = 3;

export default function WelcomeSheet() {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    // Guard against double-counting from StrictMode remounts or parent re-renders.
    // sessionStorage persists across the simulated unmount but clears when the
    // tab/PWA closes — which is what counts as a fresh "app open".
    if (sessionStorage.getItem(SESSION_FLAG)) return;
    sessionStorage.setItem(SESSION_FLAG, '1');

    const prev = parseInt(localStorage.getItem(COUNTER_KEY) || '0', 10);
    const next = prev + 1;
    localStorage.setItem(COUNTER_KEY, String(next));

    if (next % SHOW_EVERY_N !== 1) return;

    const t = setTimeout(() => setIsOpen(true), 400);
    return () => clearTimeout(t);
  }, []);

  const handleClose = () => {
    setIsOpen(false);
  };

  // Body scroll lock while open
  useEffect(() => {
    if (!isOpen) return;
    const html = document.documentElement;
    const body = document.body;
    const prevHtml = html.style.overflow;
    const prevBody = body.style.overflow;
    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    return () => {
      html.style.overflow = prevHtml;
      body.style.overflow = prevBody;
    };
  }, [isOpen]);

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[200] flex flex-col items-center justify-end pointer-events-auto">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="absolute inset-0 bg-black/50"
          />

          {/* Floating X — fades in after the sheet settles, fades out instantly on close */}
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, transition: { delay: 1.2, duration: 0.25 } }}
            exit={{ opacity: 0, transition: { duration: 0.2 } }}
            onClick={handleClose}
            className="relative mb-3 w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center z-10 active:scale-95 transition-transform"
            aria-label="Cerrar"
          >
            <X size={22} weight="bold" className="text-white" />
          </motion.button>

          {/* Sheet — ticket-style entrance: peek tip then full reveal */}
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: ['100%', '85%', '85%', '0%'] }}
            exit={{ y: '100%', transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] } }}
            transition={{
              times: [0, 0.25, 0.55, 1],
              duration: 1.3,
              ease: [0.22, 1, 0.36, 1],
            }}
            className="relative w-full max-w-md"
            style={{ maxHeight: '85dvh' }}
          >
            <img
              src="/assets/welcome-sheet.png"
              alt="Bienvenido"
              className="w-full h-auto block"
            />

            <div className="absolute inset-x-0 bottom-0 px-5 pt-16 pb-4 bg-gradient-to-t from-white via-white to-transparent">
              <button
                onClick={handleClose}
                className="w-full py-3.5 rounded-full text-base font-bold bg-brand-primary text-white active:scale-[0.98] transition-transform"
              >
                Explorar
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
